import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import type { CliCredentialsResponse, MeResponse, ResolvedTarget } from '@benilar/shared';
import type { ApiClient } from '../api.js';

interface Props {
  api: ApiClient;
}

/** The error code the API attaches to thrown errors (see ApiClient.request). */
function errorCode(e: unknown): string | undefined {
  return e && typeof e === 'object' && 'code' in e
    ? (e as { code?: string }).code
    : undefined;
}

/**
 * The employee's self-service dashboard: shows every account/role they are
 * entitled to (including children expanded from management accounts) and lets
 * them open the Console or copy short-lived CLI credentials.
 */
export function AccessDashboard({ api }: Props): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reauthRequired, setReauthRequired] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [cliCreds, setCliCreds] = useState<CliCredentialsResponse | null>(null);
  // The action to retry after a successful step-up verify (totp_required flow).
  const [pendingAction, setPendingAction] = useState<(() => Promise<void>) | null>(null);

  useEffect(() => {
    api
      .me()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [api]);

  /**
   * Map a thrown API error to the right UX: enrollment redirect, step-up modal
   * (storing `retry` to re-run after verify), reauth prompt, or a plain error.
   */
  const handleActionError = useCallback(
    (e: unknown, retry: () => Promise<void>) => {
      const code = errorCode(e);
      if (code === 'totp_enrollment_required') {
        navigate('/mfa/setup');
        return;
      }
      if (code === 'totp_required') {
        setPendingAction(() => retry);
        return;
      }
      if (code === 'reauth_required') {
        setReauthRequired(true);
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [navigate],
  );

  const openConsole = useCallback(
    async (t: ResolvedTarget) => {
      setBusyKey(`console-${t.accountId}-${t.roleName}`);
      setError(null);
      setReauthRequired(false);
      try {
        const res = await api.consoleUrl({ accountId: t.accountId, roleName: t.roleName });
        window.open(res.signInUrl, '_blank', 'noopener,noreferrer');
      } catch (e) {
        handleActionError(e, () => openConsole(t));
      } finally {
        setBusyKey(null);
      }
    },
    [api, handleActionError],
  );

  const getCli = useCallback(
    async (t: ResolvedTarget) => {
      setBusyKey(`cli-${t.accountId}-${t.roleName}`);
      setError(null);
      setReauthRequired(false);
      try {
        const res = await api.cliCredentials({ accountId: t.accountId, roleName: t.roleName });
        setCliCreds(res);
      } catch (e) {
        handleActionError(e, () => getCli(t));
      } finally {
        setBusyKey(null);
      }
    },
    [api, handleActionError],
  );

  const handleSignOut = useCallback(() => {
    void auth.removeUser();
    window.location.href = '/';
  }, [auth]);

  if (error && !data) {
    return <div className="card error">Error: {error}</div>;
  }
  if (!data) {
    return <div className="card">Loading your access…</div>;
  }

  return (
    <section>
      <h1>My access</h1>
      <p className="muted">
        Signed in as {data.identity.email}
        {data.identity.mfaAuthenticated ? ' · MFA verified' : ''}
      </p>

      {error && (
        <div className="card error">
          {error}
          {reauthRequired && (
            <div style={{ marginTop: '0.5rem' }}>
              <button className="primary" onClick={handleSignOut}>
                Sign in again
              </button>
            </div>
          )}
        </div>
      )}

      {data.targets.length === 0 ? (
        <div className="card">You have no entitlements yet. Contact a ProServ admin.</div>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Account</th>
              <th>Type</th>
              <th>Role</th>
              <th>Max session</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.targets.map((t) => {
              const key = `${t.accountId}-${t.roleName}`;
              return (
                <tr key={key}>
                  <td>{t.customerName}</td>
                  <td>
                    <code>{t.accountId}</code>
                    {t.viaManagementAccountId && (
                      <span className="badge" title={`via management ${t.viaManagementAccountId}`}>
                        child
                      </span>
                    )}
                  </td>
                  <td>{t.accountType}</td>
                  <td>{t.roleName}</td>
                  <td>{Math.round(t.maxSessionDurationSeconds / 60)} min</td>
                  <td className="actions">
                    <button
                      disabled={busyKey === `console-${key}`}
                      onClick={() => void openConsole(t)}
                    >
                      {busyKey === `console-${key}` ? '…' : 'Open console'}
                    </button>
                    <button
                      disabled={busyKey === `cli-${key}`}
                      onClick={() => void getCli(t)}
                    >
                      {busyKey === `cli-${key}` ? '…' : 'CLI creds'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {cliCreds && <CliCredsModal creds={cliCreds} onClose={() => setCliCreds(null)} />}

      {pendingAction && (
        <StepUpModal
          api={api}
          onCancel={() => setPendingAction(null)}
          onVerified={async () => {
            const retry = pendingAction;
            setPendingAction(null);
            if (retry) await retry();
          }}
        />
      )}
    </section>
  );
}

/**
 * Prompt for a 6-digit code when the step-up grace window has lapsed
 * (totp_required). On a successful verify, the caller retries the original
 * credential action.
 */
function StepUpModal({
  api,
  onVerified,
  onCancel,
}: {
  api: ApiClient;
  onVerified: () => Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      await api.mfaVerify(code);
      await onVerified();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setVerifying(false);
    }
  }, [api, code, onVerified]);

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal mfa-setup" onClick={(e) => e.stopPropagation()}>
        <h3>Verify your second factor</h3>
        <p className="muted">
          Enter the 6-digit code from your authenticator app to continue.
        </p>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="code-input"
        />
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <button
            className="primary"
            disabled={code.length !== 6 || verifying}
            onClick={() => void submit()}
          >
            {verifying ? 'Verifying…' : 'Verify'}
          </button>
          <button onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CliCredsModal({
  creds,
  onClose,
}: {
  creds: CliCredentialsResponse;
  onClose: () => void;
}): JSX.Element {
  const envBlock = [
    `export AWS_ACCESS_KEY_ID=${creds.accessKeyId}`,
    `export AWS_SECRET_ACCESS_KEY=${creds.secretAccessKey}`,
    `export AWS_SESSION_TOKEN=${creds.sessionToken}`,
    `export AWS_DEFAULT_REGION=${creds.region}`,
  ].join('\n');

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>
          Short-lived credentials · {creds.accountId} / {creds.roleName}
        </h3>
        <p className="muted">Expires {new Date(creds.expiration).toLocaleTimeString()}</p>
        <pre>{envBlock}</pre>
        <div className="actions">
          <button
            className="primary"
            onClick={() => void navigator.clipboard.writeText(envBlock)}
          >
            Copy
          </button>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
